import fs from 'fs'
import path from 'path'

import { getCiEnv } from '@anvil/common/env-ci'
import { getDirectDeepSeekApiKey } from '@anvil/common/constants/deepseek-direct'
import { z } from 'zod'


import { getApiClient, setApiClientAuthToken } from './anvil-api'
import { IS_DIRECT_MODE } from './constants'
import { getConfigDir as getConfigDirBase } from './config-dir'
import { logger } from './logger'

import type { CiEnv } from '@anvil/common/types/contracts/env'

// User schema
const userSchema = z.object({
  id: z.string().optional(),
  name: z.string(),
  email: z.string(),
  authToken: z.string(),
  fingerprintId: z.string().optional(),
  fingerprintHash: z.string().optional(),
  credits: z.number().optional(),
})

export type User = z.infer<typeof userSchema>

const credentialsSchema = z
  .object({
    default: userSchema.optional(),
  })
  .catchall(z.unknown())

// Get the config directory path.
// Re-exported as a wrapper (rather than a bare `export ... from`) so existing
// tests can still `spyOn(authModule, 'getConfigDir')`.
export const getConfigDir = (): string => getConfigDirBase()

// Get the credentials file path
export const getCredentialsPath = (): string => {
  return path.join(getConfigDir(), 'credentials.json')
}

/**
 * Parse user from JSON string
 */
const userFromJson = (
  json: string,
  profileName: string = 'default',
): User | undefined => {
  try {
    const allCredentials = credentialsSchema.parse(JSON.parse(json))
    const profile = allCredentials[profileName]
    // Validate that the profile matches the user schema
    const parsed = userSchema.safeParse(profile)
    return parsed.success ? parsed.data : undefined
  } catch (error) {
    logger.error(
      {
        errorMessage: error instanceof Error ? error.message : String(error),
        errorStack: error instanceof Error ? error.stack : undefined,
        profileName,
      },
      'Error parsing user JSON',
    )
    return
  }
}

/**
 * Get user credentials from file system
 * @returns User object or null if not found/authenticated
 */
export const getUserCredentials = (): User | null => {
  const credentialsPath = getCredentialsPath()

  // Read user credentials directly from file
  if (!fs.existsSync(credentialsPath)) {
    return null
  }

  try {
    const credentialsFile = fs.readFileSync(credentialsPath, 'utf8')
    const user = userFromJson(credentialsFile)
    return user || null
  } catch (error) {
    logger.error(
      {
        error: error instanceof Error ? error.message : String(error),
      },
      'Error reading credentials',
    )
    return null
  }
}

export type AuthTokenSource =
  | 'credentials'
  | 'environment'
  | 'direct'
  | null

/**
 * Stand-in token used in direct mode when the user has set no provider key.
 *
 * The SDK client requires *some* api key to construct, but in direct mode the
 * value is never sent anywhere: model calls authenticate with the provider key
 * (see getModelForRequest) and every backend call is disabled. Without this the
 * CLI returned a null client and failed with a misleading "check your
 * authentication" error before it ever reached the provider — so a missing key
 * looked like a broken login instead of a missing key.
 */
export const LOCAL_DIRECT_TOKEN = 'anvil-local-direct'

export interface AuthTokenDetails {
  token?: string
  source: AuthTokenSource
}

/**
 * Resolve the auth token and track where it came from.
 */
export const getAuthTokenDetails = (
  ciEnv: CiEnv = getCiEnv(),
): AuthTokenDetails => {
  const userCredentials = getUserCredentials()
  if (userCredentials?.authToken) {
    return { token: userCredentials.authToken, source: 'credentials' }
  }

  const envToken = ciEnv.ANVIL_API_KEY
  if (envToken) {
    return { token: envToken, source: 'environment' }
  }

  // Direct-to-DeepSeek mode: the DeepSeek key doubles as the auth token so
  // the CLI treats the user as authenticated without a Anvil backend.
  const deepseekToken = getDirectDeepSeekApiKey()
  if (deepseekToken) {
    return { token: deepseekToken, source: 'environment' }
  }

  // Direct mode authenticates against no backend, so a missing token must not
  // gate the CLI. Hand back a local placeholder and let the provider call
  // report the real problem if the provider key is absent.
  if (IS_DIRECT_MODE) {
    return { token: LOCAL_DIRECT_TOKEN, source: 'direct' }
  }

  return { source: null }
}

/**
 * Get the auth token from user credentials or environment variable
 */
export const getAuthToken = (): string | undefined => {
  return getAuthTokenDetails().token
}

/**
 * Check if the user has authentication credentials (but doesn't validate them)
 */
export const hasAuthCredentials = (): boolean => {
  return !!getAuthTokenDetails().token
}

export interface AuthValidationResult {
  authenticated: boolean
  hasInvalidCredentials: boolean
}

/**
 * Read existing credentials file, returns empty object if missing/invalid.
 *
 * Drops `chatgptOAuth`, which the removed ChatGPT integration wrote. Both
 * callers spread this result straight back over the file, so the dead key —
 * an OAuth access and refresh token for the user's ChatGPT account — is
 * cleaned up the next time we write for any reason. Doing it here rather than
 * in a startup pass means no extra write and no new race with login/logout;
 * nothing reads the key anymore, and with /connect gone the user has no way
 * to clear it themselves.
 */
const readCredentialsFile = (): Record<string, unknown> => {
  const credentialsPath = getCredentialsPath()
  if (!fs.existsSync(credentialsPath)) return {}
  try {
    const { chatgptOAuth: _removedIntegration, ...rest } = JSON.parse(
      fs.readFileSync(credentialsPath, 'utf8'),
    )
    return rest
  } catch {
    return {}
  }
}

/**
 * Save user credentials to file system.
 */
export const saveUserCredentials = (user: User): void => {
  const configDir = getConfigDir()
  const credentialsPath = getCredentialsPath()

  try {
    if (!fs.existsSync(configDir)) {
      fs.mkdirSync(configDir, { recursive: true })
    }

    const updatedData = { ...readCredentialsFile(), default: user }
    fs.writeFileSync(credentialsPath, JSON.stringify(updatedData, null, 2))
  } catch (error) {
    logger.error(
      {
        error: error instanceof Error ? error.message : String(error),
      },
      'Error saving credentials',
    )
    throw error
  }
}

/**
 * Clear user credentials from file system.
 * Only removes the 'default' field, preserving other credentials.
 */
export const clearUserCredentials = (): void => {
  const credentialsPath = getCredentialsPath()

  try {
    if (!fs.existsSync(credentialsPath)) return

    const { default: _, ...rest } = readCredentialsFile()

    if (Object.keys(rest).length === 0) {
      fs.unlinkSync(credentialsPath)
    } else {
      fs.writeFileSync(credentialsPath, JSON.stringify(rest, null, 2))
    }
  } catch (error) {
    logger.error(
      {
        error: error instanceof Error ? error.message : String(error),
      },
      'Error clearing credentials',
    )
    throw error
  }
}

export async function logoutUser(): Promise<boolean> {
  try {
    const user = getUserCredentials()
    // Direct mode has no backend session to revoke — only clear local state.
    if (user?.authToken && !IS_DIRECT_MODE) {
      setApiClientAuthToken(user.authToken)
      const apiClient = getApiClient()
      try {
        const response = await apiClient.logout({
          userId: user.id,
          fingerprintId: user.fingerprintId,
          fingerprintHash: user.fingerprintHash,
        })
        if (!response.ok) {
          logger.error(
            { status: response.status, error: response.error },
            'Logout request failed',
          )
        }
      } catch (err) {
        logger.error(err, 'Logout request error')
      }
    }
  } catch (error) {
    logger.error(error, 'Unexpected error preparing logout')
  }

  try {
    clearUserCredentials()
  } catch (error) {
    logger.debug({ error }, 'Failed to clear credentials during logout')
  }
  return true
}
